import React, { useEffect, useState } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';

const styleTag = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.6; }
  }
  .notif-filter-btn {
    transition: all 0.15s !important;
  }
  .notif-filter-btn:hover {
    opacity: 0.85 !important;
  }
  .notif-card {
    transition: border-color 0.2s, transform 0.15s !important;
  }
  .notif-card:hover {
    transform: translateX(2px) !important;
  }
  .notif-dismiss-btn {
    transition: color 0.15s, opacity 0.15s !important;
  }
  .notif-dismiss-btn:hover {
    color: #E05C5C !important;
    opacity: 1 !important;
  }
  .notif-clear-btn {
    transition: background-color 0.2s, border-color 0.2s !important;
  }
  .notif-clear-btn:hover:not(:disabled) {
    background-color: rgba(224,92,92,0.1) !important;
    border-color: rgba(224,92,92,0.5) !important;
  }
  .notif-pref-row {
    transition: border-color 0.2s, background-color 0.2s !important;
  }
  .notif-pref-row:hover {
    border-color: rgba(0,128,129,0.25) !important;
    background-color: rgba(0,128,129,0.04) !important;
  }
  .notif-toggle {
    transition: background-color 0.25s !important;
  }
`;

export default function NotificationsView({ user, userData, theme, t: themeTokens, translate: t }) {
  const dark = theme === 'dark';

  const colors = {
    cardBg:           dark ? 'rgba(255,255,255,0.02)' : '#FFFFFF',
    cardBorder:       dark ? 'rgba(255,255,255,0.07)' : '#e5e7eb',
    text:             dark ? '#ffffff' : '#1B2D44',
    subtext:          dark ? 'rgba(255,255,255,0.35)' : '#6b7280',
    mutedText:        dark ? 'rgba(255,255,255,0.3)' : '#9ca3af',
    labelColor:       dark ? 'rgba(255,255,255,0.4)' : '#6b7280',
    emptyText:        dark ? 'rgba(255,255,255,0.25)' : '#9ca3af',
    filterInactiveBg: dark ? 'rgba(255,255,255,0.06)' : '#f3f4f6',
    filterInactiveText: dark ? 'rgba(255,255,255,0.45)' : '#6b7280',
    notifReadBg:      dark ? 'rgba(255,255,255,0.02)' : '#f9fafb',
    notifUnreadBg:    dark ? 'rgba(255,255,255,0.05)' : '#f0fafa',
    notifReadBorder:  dark ? 'rgba(255,255,255,0.05)' : '#e5e7eb',
    notifUnreadBorder:dark ? 'rgba(255,255,255,0.1)' : '#d1fafa',
    notifReadText:    dark ? 'rgba(255,255,255,0.55)' : '#6b7280',
    notifUnreadText:  dark ? 'rgba(255,255,255,0.85)' : '#1B2D44',
    timeBadgeBg:      dark ? 'rgba(255,255,255,0.05)' : '#f3f4f6',
    timeText:         dark ? 'rgba(255,255,255,0.25)' : '#9ca3af',
    dismissColor:     dark ? 'rgba(255,255,255,0.2)' : '#d1d5db',
    prefRowBg:        dark ? 'rgba(255,255,255,0.03)' : '#f9fafb',
    prefRowBorder:    dark ? 'rgba(255,255,255,0.06)' : '#e5e7eb',
    prefLabelMuted:   dark ? 'rgba(255,255,255,0.35)' : '#9ca3af',
    prefLabelActive:  dark ? 'rgba(255,255,255,0.85)' : '#1B2D44',
    toggleOffBg:      dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb',
  };

  const [notifications, setNotifications] = useState([]);
  const [mutedTypes, setMutedTypes] = useState(userData?.mutedNotifications || []);
  const [activeTab, setActiveTab] = useState('all');
  const [clearing, setClearing] = useState(false);

  // The `partner_message` id must match PARTNER_MESSAGE_TYPE in
  // functions/index.js: the server checks users.mutedNotifications for
  // this exact id before it creates a "your partner sent a message" alert.
  // Its label reuses the existing "Chat" translation.
  const NOTIFICATION_TYPES = [
    { id: 'partner_message', label: t('chat'),            icon: '💬', color: '#38bdf8', shadow: 'rgba(56,189,248,0.3)' },
    { id: 'match_found',     label: t('notifMatchFound'),   icon: '🎯', color: '#008081', shadow: 'rgba(0,128,129,0.3)' },
    { id: 'goal_achieved',   label: t('notifGoalAchieved'), icon: '🏆', color: '#c084fc', shadow: 'rgba(192,132,252,0.3)' },
    { id: 'support',         label: t('notifSupport'),      icon: '💪', color: '#4ade80', shadow: 'rgba(74,222,128,0.3)' },
    { id: 'unmatched',       label: t('notifUnmatched'),    icon: '🔓', color: '#f87171', shadow: 'rgba(248,113,113,0.3)' },
    { id: 'match_request',   label: t('notifMatchRequest'), icon: '🤝', color: '#facc15', shadow: 'rgba(250,204,21,0.3)' },
  ];

  useEffect(() => {
    if (!user) return;
    // Fixed: use 'createdAt' to match what Cloud Functions write,
    // eliminating the missing composite index error on 'timestamp'.
    const q = query(
      collection(db, 'notifications'),
      where('recipientId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items = [];
      snapshot.forEach(d => items.push({ id: d.id, ...d.data() }));
      setNotifications(items);
      snapshot.forEach(d => {
        if (!d.data().read) {
          updateDoc(doc(db, 'notifications', d.id), { read: true });
        }
      });
    });
    return () => unsubscribe();
  }, [user]);

  const toggleMute = async (typeId) => {
    const newMuted = mutedTypes.includes(typeId)
      ? mutedTypes.filter(id => id !== typeId)
      : [...mutedTypes, typeId];
    setMutedTypes(newMuted);
    try {
      await updateDoc(doc(db, 'users', user.uid), { mutedNotifications: newMuted });
    } catch (err) {
      console.error('Error saving mute preference:', err);
    }
  };

  const deleteNotification = async (notifId) => {
    try {
      await deleteDoc(doc(db, 'notifications', notifId));
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const clearAll = async () => {
    setClearing(true);
    try {
      const batch = writeBatch(db);
      notifications.forEach(n => batch.delete(doc(db, 'notifications', n.id)));
      await batch.commit();
    } catch (err) {
      console.error('Error clearing notifications:', err);
    } finally {
      setClearing(false);
    }
  };

  const getTypeInfo = (type) => {
    return NOTIFICATION_TYPES.find(nt => nt.id === type) || { icon: '🔔', color: '#9ca3af', shadow: 'transparent' };
  };

  // Builds the display text for a notification. Several types store the
  // raw partner name instead of a pre-built English sentence, so the
  // sentence is assembled here via t(), which lets it follow the viewer's
  // current language:
  //   match_found     -> notifMatchFoundMessage   (set by findMatch)
  //   partner_message -> newMessageNotification   (set by notifyPartnerMessage)
  //   unmatched       -> unmatchMessage           (set by unmatch)
  // Older records (written before this change) only have a `message`
  // field already baked in English — those fall through to `notif.message`
  // and keep showing in English, since the original text was never stored
  // as translatable data.
  const getNotifText = (notif) => {
    if (notif.type === 'match_found' && notif.partnerName !== undefined) {
      const name = notif.partnerName || t('someone');
      return t('notifMatchFoundMessage').replace('{name}', name);
    }
    if (notif.type === 'partner_message') {
      const name = notif.partnerName || t('yourPartner');
      return t('newMessageNotification', { name });
    }
    if (notif.type === 'unmatched' && !notif.message) {
      const name = notif.partnerName || t('yourPartner');
      return t('unmatchMessage', { name });
    }
    return notif.message;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return t('justNow');
    if (mins < 60) return t('minsAgo').replace('{count}', mins);
    if (hours < 24) return t('hoursAgo').replace('{count}', hours);
    return t('daysAgo').replace('{count}', days);
  };

  const visibleNotifications = notifications.filter(n =>
    activeTab === 'all' || n.type === activeTab
  );
  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <>
      <style>{styleTag}</style>
      <div style={{ maxWidth: '42rem', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

        {/* Header card */}
        <div style={{
          background: colors.cardBg,
          backdropFilter: 'blur(10px)',
          borderRadius: '1.25rem',
          padding: '1.75rem',
          border: `1px solid ${colors.cardBorder}`,
          animation: 'fadeInUp 0.35s ease'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
            <div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '800', color: colors.text, marginBottom: '0.25rem', letterSpacing: '-0.02em' }}>
                {t('notificationsTitle')}
              </h2>
              <p style={{ fontSize: '0.8rem', color: colors.subtext, margin: 0 }}>
                {t('notificationsCount')
                  .replace('{total}', notifications.length)
                  .replace('{unread}', unreadCount)}
              </p>
            </div>
            {notifications.length > 0 && (
              <button
                className="notif-clear-btn"
                onClick={clearAll}
                disabled={clearing}
                style={{
                  fontSize: '0.8rem', color: '#f87171',
                  background: 'transparent',
                  border: '1px solid rgba(248,113,113,0.3)',
                  borderRadius: '0.5rem', padding: '0.4rem 0.875rem',
                  cursor: clearing ? 'not-allowed' : 'pointer',
                  opacity: clearing ? 0.5 : 1, fontWeight: '600'
                }}
              >
                {clearing ? t('clearing') : t('clearAll')}
              </button>
            )}
          </div>

          {/* Filter tabs */}
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            <button
              className="notif-filter-btn"
              onClick={() => setActiveTab('all')}
              style={{
                padding: '0.35rem 0.875rem', borderRadius: '9999px', border: 'none',
                backgroundColor: activeTab === 'all' ? '#008081' : colors.filterInactiveBg,
                color: activeTab === 'all' ? '#ffffff' : colors.filterInactiveText,
                fontWeight: activeTab === 'all' ? '700' : '400',
                cursor: 'pointer', fontSize: '0.78rem',
                boxShadow: activeTab === 'all' ? '0 4px 12px rgba(0,128,129,0.35)' : 'none'
              }}
            >
              {t('all')} ({notifications.length})
            </button>
            {NOTIFICATION_TYPES.map(type => {
              const count = notifications.filter(n => n.type === type.id).length;
              if (count === 0) return null;
              const isActive = activeTab === type.id;
              return (
                <button
                  key={type.id}
                  className="notif-filter-btn"
                  onClick={() => setActiveTab(type.id)}
                  style={{
                    padding: '0.35rem 0.875rem', borderRadius: '9999px', border: 'none',
                    backgroundColor: isActive ? type.color : colors.filterInactiveBg,
                    color: isActive ? '#ffffff' : colors.filterInactiveText,
                    fontWeight: isActive ? '700' : '400',
                    cursor: 'pointer', fontSize: '0.78rem',
                    boxShadow: isActive ? `0 4px 12px ${type.shadow}` : 'none'
                  }}
                >
                  {type.icon} {type.label} ({count})
                </button>
              );
            })}
          </div>
        </div>

        {/* Notifications list */}
        <div style={{
          background: colors.cardBg,
          backdropFilter: 'blur(10px)',
          borderRadius: '1.25rem',
          padding: '1.75rem',
          border: `1px solid ${colors.cardBorder}`,
          animation: 'fadeInUp 0.4s ease'
        }}>
          {visibleNotifications.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem 0', color: colors.emptyText }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🔔</div>
              <p style={{ fontSize: '0.875rem', margin: 0 }}>{t('noNotifications')}</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '0.6rem' }}>
              {visibleNotifications.map(notif => {
                const typeInfo = getTypeInfo(notif.type);
                return (
                  <div
                    key={notif.id}
                    className="notif-card"
                    style={{
                      padding: '1rem 1.1rem',
                      borderRadius: '0.875rem',
                      border: `1px solid ${notif.read ? colors.notifReadBorder : colors.notifUnreadBorder}`,
                      borderLeft: `3px solid ${typeInfo.color}`,
                      backgroundColor: notif.read ? colors.notifReadBg : colors.notifUnreadBg,
                      display: 'flex', gap: '0.875rem', alignItems: 'flex-start'
                    }}
                  >
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
                      background: `linear-gradient(135deg, ${typeInfo.color}22, ${typeInfo.color}11)`,
                      border: `1px solid ${typeInfo.color}33`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '1rem'
                    }}>
                      {typeInfo.icon}
                    </div>

                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: '0.875rem', color: notif.read ? colors.notifReadText : colors.notifUnreadText, margin: '0 0 0.3rem', lineHeight: 1.55 }}>
                        {getNotifText(notif)}
                      </p>
                      <span style={{
                        fontSize: '0.72rem',
                        color: colors.timeText,
                        backgroundColor: colors.timeBadgeBg,
                        padding: '0.1rem 0.5rem', borderRadius: '9999px'
                      }}>
                        {formatTime(notif.createdAt)}
                      </span>
                    </div>

                    <button
                      className="notif-dismiss-btn"
                      onClick={() => deleteNotification(notif.id)}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: colors.dismissColor, fontSize: '1rem',
                        flexShrink: 0, padding: '0.1rem', lineHeight: 1
                      }}
                    >
                      {t('dismiss')}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Mute preferences card */}
        <div style={{
          background: colors.cardBg,
          backdropFilter: 'blur(10px)',
          borderRadius: '1.25rem',
          padding: '1.75rem',
          border: `1px solid ${colors.cardBorder}`,
          animation: 'fadeInUp 0.45s ease'
        }}>
          <h3 style={{ fontSize: '0.75rem', fontWeight: '700', color: colors.labelColor, marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            {t('notificationPrefs')}
          </h3>
          <p style={{ fontSize: '0.8rem', color: colors.mutedText, marginBottom: '1.25rem', lineHeight: 1.6 }}>
            {t('notificationPrefsDesc')}
          </p>
          <div style={{ display: 'grid', gap: '0.6rem' }}>
            {NOTIFICATION_TYPES.map(type => {
              const isMuted = mutedTypes.includes(type.id);
              return (
                <div
                  key={type.id}
                  className="notif-pref-row"
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '0.875rem 1rem',
                    backgroundColor: colors.prefRowBg,
                    borderRadius: '0.875rem',
                    border: `1px solid ${colors.prefRowBorder}`
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '0.5rem',
                      background: `linear-gradient(135deg, ${type.color}22, ${type.color}11)`,
                      border: `1px solid ${type.color}33`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '1rem', flexShrink: 0
                    }}>
                      {type.icon}
                    </div>
                    <div>
                      <div style={{ fontWeight: '600', color: isMuted ? colors.prefLabelMuted : colors.prefLabelActive, fontSize: '0.875rem' }}>
                        {type.label}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: isMuted ? colors.mutedText : type.color, fontWeight: '500', marginTop: '0.1rem' }}>
                        {isMuted ? t('muted') : t('active')}
                      </div>
                    </div>
                  </div>

                  <button
                    className="notif-toggle"
                    onClick={() => toggleMute(type.id)}
                    style={{
                      width: '44px', height: '24px', borderRadius: '9999px', border: 'none',
                      backgroundColor: isMuted ? colors.toggleOffBg : type.color,
                      cursor: 'pointer', position: 'relative', flexShrink: 0,
                      boxShadow: isMuted ? 'none' : `0 0 10px ${type.shadow}`
                    }}
                  >
                    <div style={{
                      width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#ffffff',
                      position: 'absolute', top: '3px',
                      left: isMuted ? '3px' : '23px',
                      transition: 'left 0.2s',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.3)'
                    }} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </>
  );
}